import React, { useState } from 'react';

import { isEmpty } from 'lodash';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Input, Slider, Tooltip } from 'antd';
import { SliderMarks } from 'antd/lib/slider';
import { getWalletSliderMaxValuePosition, RootState } from '@share/utils';
import { ILoginState } from '@share/store/slices';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faInfoCircle, faWallet } from '@fortawesome/free-solid-svg-icons';

import './style.scss';
import { GetCapitalWalletLog } from '@utils';

interface IMapStateToProps {
  loginStore: ILoginState;
}

interface IProps extends IMapStateToProps, WrappedComponentProps {
  display: boolean;

  selectedClientCash: number | null | undefined;

  titleLabel?: string;

  onSelectClientCash: (selectedClientCash: number | null | undefined) => void;
}

function WalletSearchSliderComponent(props: IProps) {
  const [collapse, setCollapse] = useState(false);

  const { display, selectedClientCash, titleLabel, loginStore, onSelectClientCash, intl } = props;
  const { account, userWalletData, lifeStyle } = loginStore;

  if (!display) {
    return null;
  }

  const walletNoDecimals = account?.walletNoDecimals;
  const background: any = account?.buttonColor ? account?.buttonColor : '#0081FE';

  const maxWalletAmount = userWalletData?.balance;
  const maxSlider = maxWalletAmount ? 
                      walletNoDecimals ? 
                        Math.floor(maxWalletAmount) : 
                        maxWalletAmount ? Math.floor(maxWalletAmount * 100) / 100 : 0 : 
                      0;

  const marks: SliderMarks = {
    0: {
      style: {
        transform: 'translate(-4px, -40px)'
      },
      label: '0',
    }
  };

  if (maxSlider) {
    marks[maxSlider] = {
      style: {
        transform: `translate(${walletNoDecimals ? getWalletSliderMaxValuePosition(maxSlider) :'-27'}px, -40px)`,
        left: 'calc(100% - 5px)',
      },
      label: `${maxSlider?.toLocaleString('en-US')}`,
    };
  }

  const handleCollapse = () => {
    setCollapse(!collapse);
  }

  const logo = GetCapitalWalletLog(lifeStyle);
  const isCapital = account?.isCapitalVacations;

  const explanationTooltipText = maxWalletAmount
    ? intl.formatMessage(
        { id: 'wallet.client.cash.search_slider.message' },
        {
          amount: (userWalletData?.maxClientCashPercentageAllow || 0) * 100,
          amountActive: userWalletData?.balance?.toLocaleString('en-US'),
          strong: (message: string) => (<strong>{message}</strong>)
}
      )
    : intl.formatMessage(
        { id: 'wallet.client.cash.no_client_cash.message' },
        { amountActive: userWalletData?.balance?.toLocaleString('en-US'), clientCash: account?.walletClientCashName }
      );

  return (
    <div className="wallet-search-slider container-fluid" style={{ position: 'relative' }}>
      <div className="wallet-search-slider__collapse" onClick={handleCollapse}>
        <FontAwesomeIcon icon={collapse ? faAngleDown : faAngleUp} />
      </div>
      
      <div className="row">
        <div className={`wallet-search-slider__header col-md-12 ${isCapital ? 'capital-header-slider' : ''}`}>
          <p className={`wallet-search-slider__title ${collapse ? 'collapsed' : ''} ${isCapital ? 'capital' : ''}`} style={{ color: background }}>
            <FormattedMessage
              id={titleLabel ? titleLabel : 'wallet.client.cash.want_use'}
              values={{ clientCashName: account?.walletClientCashName }}
            />
            <Tooltip title={explanationTooltipText} placement="top">
              <span className="wallet-search-slider__info-icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
            </Tooltip>
          </p>
          {isCapital ? <img className="wallet-search-slider__logo" src={logo} alt="LIFESTYLES" /> : null}
        </div>

        {!collapse ? (
          <>
            <div className="wallet-search-slider__balance-badge">
              <span>
                <FontAwesomeIcon icon={faWallet} className="wallet-search-slider__balance-icon" style={{ color: background }} />
                <FormattedMessage
                  id="wallet.client.cash.search_slider.message2"
                  values={{ 
                    amountActive: maxSlider?.toLocaleString('en-US') ?? '0', 
                    clientCash: account?.walletClientCashName || 'Points' 
                  }}
                  />
              </span>
            </div>

            <div className={`wallet-search-slider__controls col-md-12 ${isCapital ? 'capital' : ''}`}>
              <div className="wallet-search-slider__slider-wrap">
                <Slider
                  min={0}
                  max={maxSlider}
                  marks={marks}
                  defaultValue={0}
                  step={walletNoDecimals ? 1 : 0.01}
                  tooltip={{ open: false }}
                  value={selectedClientCash && selectedClientCash >= 0 ? selectedClientCash : 0}
                  onChange={(value: any) => onSelectClientCash(value)}
                  trackStyle={{ backgroundColor: background }}
                  handleStyle={{ borderColor: background, backgroundColor: background }}
                  disabled={!!maxWalletAmount && maxWalletAmount <= 0}
                  style={{ marginTop: '30px' }}
                  className="wallet-search-slider__slider"
                />
              </div>
              <Input
                value={selectedClientCash && selectedClientCash >= 0 ? selectedClientCash.toLocaleString('en-US') : ''}
                className={`room-info__client-cash-input ${(selectedClientCash || 0) > (maxWalletAmount || 0) ? 'invalid' : ''}`}
                onChange={e => {
                  const value = !isEmpty(e.target.value) ? Number(e.target.value.replace(/,/g, '').replace(/\./g, '')) : null;
                  onSelectClientCash(value);
                }}
                onKeyPress={event => { if (!/[0-9]/.test(event.key)) event.preventDefault(); }}
                disabled={!!maxWalletAmount && maxWalletAmount <= 0}
              />
            </div>

            {(selectedClientCash || 0) > (maxWalletAmount || 0) && (
              <div className="wallet-search-slider__note invalid col-md-12" style={{ marginBottom: '10px' }}>
                <FormattedMessage id="wallet.client.cash.invalid" values={{ amount: maxSlider?.toLocaleString('en-US'), clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName }} />
              </div>)}

            {!!selectedClientCash && walletNoDecimals && selectedClientCash <= (maxWalletAmount || 0) && Math.floor(selectedClientCash) !== selectedClientCash && (
              <div className="wallet-input__note invalid col-md-12" style={{ marginBottom: '10px' }}>
                <FormattedMessage id="wallet.client.cash.only_decimals" values={{ clientCash: isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName }} />
              </div>)}

            <div className={`wallet-search-slider__explanation col-md-12 ${isCapital ? 'capital' : ''}`}>
              {maxWalletAmount ? (
                <FormattedMessage
                  id="wallet.client.cash.search_slider.message"
                  values={{ 
                    amount: (userWalletData?.maxClientCashPercentageAllow || 0) * 100,
                    amountActive: userWalletData?.balance?.toLocaleString('en-US'),
                    strong: (message: string) => (<strong>{message}</strong>)
                  }}
                />
              ) : (
                <FormattedMessage
                  id="wallet.client.cash.no_client_cash.message"
                  values={{ amountActive: userWalletData?.balance?.toLocaleString('en-US'), clientCash: account?.walletClientCashName }}
                />
              )}
            </div>
          </>) : null}
      </div>
    </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore
  };
};

export const WalletSearchSlider = connect(mapStateToProps)(injectIntl(WalletSearchSliderComponent));

